/**
 * Reads a frame trace file exported by a workshop tracer, in the OMA/VCA Data Communication Standard
 * (DCS) text format, for the lens dispensing trace card.
 *
 * A DCS file is a list of LABEL=value records, one per line. The header gives the box (HBOX, VBOX),
 * the bridge (DBL), the effective diameter (FED, or ED), the circumference (CIRC) and the frame curve
 * (FCRV); a record with a value per eye holds them as "right;left". Each traced lens then has a TRCFMT
 * record - format;count;E|U;R|L|B;F|P - followed by R= records holding its radii in hundredths of a
 * millimetre, several to a line, until there are `count` of them. A file with both lenses has two
 * TRCFMT blocks; side B is one trace used for both.
 *
 * Only format 1 (radii written out as ASCII numbers) is read for now. Radii are equally spaced in
 * angle (E) unless the block gives its own A= angles, in hundredths of a degree (U).
 *
 * Angles: radius 0 points to the eye's temporal side along the horizontal, and the angle increases
 * counter-clockwise as the wearer sees it, so 90 degrees is the top of the lens. The left lens uses
 * the same convention mirrored. This needs checking against a real tracer's export - see
 * lensPoint below, the only place it is applied. LensTraceOutline on the server does the same.
 *
 * The whole file is kept with the trace (as `outline`) so the order PDF can draw the shape.
 */
app.service("LensTraceParser", [
	function () {
		// The largest file read. A two-lens trace of 1000 radii each is about 12 KB.
		const MAX_BYTES = 200 * 1024;
		// What the file picker offers.
		const ACCEPT = ".oma,.vca,.dat,.txt";

		const number = (v) => {
			const text = String(v === undefined || v === null ? "" : v).trim();
			return text !== "" && isFinite(text) ? Number(text) : null;
		};

		// A record with a value per eye - "52.20;52.18" - or one value for both.
		const perEye = (value) => {
			const parts = String(value || "").split(";");
			const right = number(parts[0]);
			return { right: right, left: parts.length > 1 ? number(parts[1]) : right };
		};

		/**
		 * The records and traced lenses in a file's text. Labels are read in any case, and the
		 * framing characters and request records (REQ=, ?=) some tracers send are skipped.
		 */
		function read(text) {
			const records = {};
			const blocks = [];
			let block = null;
			String(text)
				.split(/\r\n|\r|\n/)
				.forEach(function (rawLine) {
					// Drop control characters: tracers on a serial link frame each record with FS/GS/RS.
					const line = rawLine.replace(/[\u0000-\u001f\u007f]/g, "").trim();
					const eq = line.indexOf("=");
					if (eq <= 0) return;
					const label = line.slice(0, eq).trim().toUpperCase();
					const value = line.slice(eq + 1).trim();
					if (label === "TRCFMT") {
						const f = value.split(";").map((p) => p.trim().toUpperCase());
						block = { format: f[0], count: number(f[1]), spacing: f[2] || "E", side: f[3] || "", radii: [], angles: [] };
						blocks.push(block);
					} else if (label === "R" || label === "A") {
						// Radii (or angles) carry on over as many lines as it takes. Any before a TRCFMT
						// belong to no lens.
						if (!block) return;
						const into = label === "R" ? block.radii : block.angles;
						value.split(";").forEach(function (v) {
							if (v.trim() !== "") into.push(number(v));
						});
					} else if (!(label in records)) {
						records[label] = value;
					}
				});
			return { records: records, blocks: blocks };
		}

		const sideName = { R: "right", L: "left" };

		// Checks a lens's block and turns it into radii (mm) and angles (radians).
		function lensFrom(block) {
			const name = block.side === "L" ? "left lens" : block.side === "R" ? "right lens" : "lens";
			if (block.format !== "1") {
				return { error: `This trace is in DCS format ${block.format || "(none)"}. Only format 1 (ASCII radii) can be imported for now - set the tracer to export ASCII radii and trace again.` };
			}
			if (!block.count || block.count < 8 || block.count > 10000 || Math.floor(block.count) !== block.count) {
				return { error: `The ${name}'s trace doesn't say how many radii it has.` };
			}
			if (block.radii.length < block.count) {
				return { error: `The ${name}'s outline is incomplete: it has ${block.radii.length} of its ${block.count} radii.` };
			}
			const radii = block.radii.slice(0, block.count);
			if (radii.some((r) => r === null || r <= 0 || r > 6000)) {
				return { error: `The ${name}'s outline has a radius that isn't a lens size (each should be a positive number in hundredths of a mm).` };
			}
			let angles;
			if (block.spacing === "U") {
				if (block.angles.length < block.count || block.angles.slice(0, block.count).some((a) => a === null)) {
					return { error: `The ${name}'s outline is missing the angles of its radii.` };
				}
				angles = block.angles.slice(0, block.count).map((a) => ((a / 100) * Math.PI) / 180);
			} else {
				angles = radii.map((r, i) => (2 * Math.PI * i) / block.count);
			}
			return { radii: radii.map((r) => r / 100), angles: angles };
		}

		/**
		 * Parses a trace file. Returns `{ trace, lenses }` - the trace in the shape the dispensing job
		 * keeps it, and each traced lens's radii - or `{ error }` with a message for the user.
		 */
		function parse(text) {
			if (typeof text !== "string" || !text.trim()) return { error: "The file is empty." };
			if (text.length > MAX_BYTES) return { error: "The file is too large for a frame trace (over 200 KB)." };
			const parsed = read(text);
			const rec = parsed.records;
			const hbox = perEye(rec.HBOX);
			const vbox = perEye(rec.VBOX);
			if (hbox.right === null && hbox.left === null) {
				return { error: "This file has no box width (HBOX), so it isn't a frame trace CatTrax can read. Export the trace as an OMA/VCA file." };
			}
			if (vbox.right === null && vbox.left === null) {
				return { error: "This file has no box height (VBOX), so it isn't a frame trace CatTrax can read. Export the trace as an OMA/VCA file." };
			}
			const traced = parsed.blocks.filter((b) => b.radii.length || b.format !== "1");
			if (!traced.length) {
				return { error: "This file has the frame's box but no traced outline (no TRCFMT and R= radii). Export the trace with its outline." };
			}

			const lenses = { right: null, left: null };
			for (let i = 0; i < traced.length; i++) {
				const block = traced[i];
				if (!/^[RLB]$/.test(block.side)) return { error: "The file's trace doesn't say which lens it is (R, L or B)." };
				const lens = lensFrom(block);
				if (lens.error) return { error: lens.error };
				// Side B is one trace for both lenses; a lens traced on its own wins over it.
				if (block.side === "B") {
					lenses.right = lenses.right || lens;
					lenses.left = lenses.left || lens;
				} else {
					lenses[sideName[block.side]] = lens;
				}
			}

			// Which lenses were traced decides the mode, and which eye's figures a per-eye record gives.
			const both = !!(lenses.right && lenses.left);
			const mode = both ? "Binocular" : lenses.right ? "Right" : "Left";
			const pick = (v) => (lenses.right ? (v.right !== null ? v.right : v.left) : v.left !== null ? v.left : v.right);
			const device = [rec.VEN, rec.MODEL].map((v) => (v || "").trim()).filter(Boolean).join(" ") || (rec.DEV || "").trim() || "Frame tracer";

			return {
				trace: {
					a: pick(hbox),
					b: pick(vbox),
					dbl: number(rec.DBL),
					ed: pick(perEye(rec.FED !== undefined ? rec.FED : rec.ED)),
					circumference: pick(perEye(rec.CIRC)),
					frameCurve: pick(perEye(rec.FCRV)),
					device: device,
					mode: mode,
					format: "OMA",
					outline: text,
					tracedAt: new Date(),
				},
				lenses: lenses,
			};
		}

		/**
		 * One point of a lens's outline, in mm, as seen facing the patient (x to the right, y up): the
		 * patient's right lens is on the left, so its temporal side - angle 0 - points left. The left
		 * lens is the mirror image, its angle 0 pointing right.
		 */
		function lensPoint(side, r, angle) {
			const x = r * Math.cos(angle);
			return { x: side === "right" ? -x : x, y: r * Math.sin(angle) };
		}

		const round = (v) => Math.round(v * 100) / 100;

		/**
		 * What the trace card draws for a trace's outline: an SVG viewBox and a path per lens, in mm,
		 * the lenses set apart by the DBL. Null when there is no outline or it can't be read.
		 */
		function preview(outline, dbl) {
			if (!outline) return null;
			const parsed = parse(outline);
			if (parsed.error) return null;
			const gap = number(dbl) > 0 ? number(dbl) : parsed.trace.dbl > 0 ? parsed.trace.dbl : 18;
			const lenses = [];
			let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
			["right", "left"].forEach(function (side) {
				const lens = parsed.lenses[side];
				if (!lens) return;
				const points = lens.radii.map((r, i) => lensPoint(side, r, lens.angles[i]));
				// Each lens's nasal edge sits half the DBL from the middle.
				const xs = points.map((p) => p.x);
				const shift = side === "right" ? -gap / 2 - Math.max.apply(null, xs) : gap / 2 - Math.min.apply(null, xs);
				const d = points.map((p, i) => (i ? "L" : "M") + round(p.x + shift) + " " + round(-p.y)).join(" ") + " Z";
				points.forEach(function (p) {
					minX = Math.min(minX, p.x + shift);
					maxX = Math.max(maxX, p.x + shift);
					minY = Math.min(minY, -p.y);
					maxY = Math.max(maxY, -p.y);
				});
				lenses.push({ side: side === "right" ? "R" : "L", d: d, labelX: round(shift) });
			});
			const pad = 3;
			minX = Math.min(minX, -gap / 2 - 1);
			maxX = Math.max(maxX, gap / 2 + 1);
			return {
				viewBox: [minX - pad, minY - pad, maxX - minX + 2 * pad, maxY - minY + 2 * pad].map(round).join(" "),
				lenses: lenses,
				// The horizontal through the lens centres.
				datum: { x1: round(minX - pad), x2: round(maxX + pad) },
			};
		}

		return { MAX_BYTES: MAX_BYTES, ACCEPT: ACCEPT, parse: parse, preview: preview, lensPoint: lensPoint };
	},
]);
